import { useState, useEffect, useCallback, useRef } from 'react';
import { useLabels } from '../hooks/useLabels';
import LabelForm from './LabelForm';
import LabelCard from './LabelCard';
import Modal from '../../../components/Modal';
import Button from '../../../components/Button';
import FormActions from '../../../components/FormActions';
import Loading from '../../uiStatus/components/Loading';
import { formShell } from '../../../styles/formShell';

const LabelManager = ({ projectId, onSuccess, onError }) => {
    const {
        labels,
        loading,
        error,
        message,
        addLabel,
        editLabel,
        removeLabel,
        clearMessage,
        loadLabels
    } = useLabels(projectId);

    const [editingLabel, setEditingLabel] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [originalLabels, setOriginalLabels] = useState([]);
    const [localLabels, setLocalLabels] = useState([]);
    const [pendingChanges, setPendingChanges] = useState({
        added: [],
        edited: [],
        deleted: []
    });
    const isInitialized = useRef(false);

    useEffect(() => {
        if (message.text) {
            const timer = setTimeout(() => {
                clearMessage();
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [message.text, message.type, clearMessage]);

    useEffect(() => {
        if (labels.length > 0) {
            const labelsCopy = JSON.parse(JSON.stringify(labels));
            if (!isInitialized.current) {
                setOriginalLabels(labelsCopy);
                isInitialized.current = true;
            }
            setLocalLabels(labelsCopy);
        }
    }, [labels]);

    const handleSubmit = useCallback((data) => {
        const isDuplicate = localLabels.some(label => {
            if (editingLabel && label.id === editingLabel.id) {
                return false;
            }
            return label.name.toLowerCase().trim() === data.name.toLowerCase().trim();
        });

        if (isDuplicate) {
            return false;
        }

        if (editingLabel) {
            const updatedLabels = localLabels.map(label =>
                label.id === editingLabel.id
                    ? { ...label, name: data.name, color: data.color }
                    : label
            );
            setLocalLabels(updatedLabels);

            setPendingChanges(prev => {
                const existingEdit = prev.edited.findIndex(e => e.id === editingLabel.id);
                if (existingEdit >= 0) {
                    const newEdited = [...prev.edited];
                    newEdited[existingEdit] = { id: editingLabel.id, ...data };
                    return { ...prev, edited: newEdited };
                }
                return { ...prev, edited: [...prev.edited, { id: editingLabel.id, ...data }] };
            });

            setEditingLabel(null);
            setShowModal(false);
            return true;
        }

        const tempId = `temp-${Date.now()}`;
        const newLabel = { id: tempId, ...data };
        setLocalLabels(prev => [...prev, newLabel]);
        setPendingChanges(prev => ({
            ...prev,
            added: [...prev.added, newLabel]
        }));
        setShowModal(false);
        return true;
    }, [editingLabel, localLabels]);

    const handleEdit = useCallback((label) => {
        setEditingLabel(label);
        setShowModal(true);
        clearMessage();
    }, [clearMessage]);

    const handleDelete = useCallback((id) => {
        const isTempId = id.toString().startsWith('temp-');

        if (isTempId) {
            setLocalLabels(prev => prev.filter(label => label.id !== id));
            setPendingChanges(prev => ({
                ...prev,
                added: prev.added.filter(label => label.id !== id)
            }));
        } else {
            setLocalLabels(prev => prev.filter(label => label.id !== id));
            setPendingChanges(prev => ({
                ...prev,
                deleted: [...prev.deleted, id],
                edited: prev.edited.filter(e => e.id !== id)
            }));
        }

        return true;
    }, []);

    const hasChanges = useCallback(() => {
        return pendingChanges.added.length > 0 ||
               pendingChanges.edited.length > 0 ||
               pendingChanges.deleted.length > 0;
    }, [pendingChanges]);

    const handleAddNew = useCallback(() => {
        setEditingLabel(null);
        setShowModal(true);
        clearMessage();
    }, [clearMessage]);

    const handleCancel = useCallback(() => {
        setEditingLabel(null);
        setShowModal(false);
        clearMessage();
    }, [clearMessage]);

    const handleReset = useCallback(() => {
        setLocalLabels(JSON.parse(JSON.stringify(originalLabels)));
        setPendingChanges({ added: [], edited: [], deleted: [] });
    }, [originalLabels]);

    const handleSave = useCallback(async () => {
        try {
            for (const id of pendingChanges.deleted) {
                const success = await removeLabel(id);
                if (!success) {
                    throw new Error(`ラベルの削除に失敗しました: ${id}`);
                }
            }

            for (const edit of pendingChanges.edited) {
                const success = await editLabel(edit.id, { name: edit.name, color: edit.color });
                if (!success) {
                    throw new Error(`ラベルの更新に失敗しました: ${edit.id}`);
                }
            }

            for (const add of pendingChanges.added) {
                const success = await addLabel({ name: add.name, color: add.color });
                if (!success) {
                    throw new Error(`ラベルの追加に失敗しました: ${add.name}`);
                }
            }

            setPendingChanges({ added: [], edited: [], deleted: [] });
            await loadLabels();

            if (onSuccess) {
                onSuccess('save');
            }
        } catch (saveError) {
            console.error('保存エラー:', saveError);
            if (onError) {
                onError('save');
            }
        }
    }, [pendingChanges, removeLabel, editLabel, addLabel, loadLabels, onSuccess, onError]);

    if (!projectId) {
        return (
            <p className={formShell.error}>プロジェクトIDが無効です。</p>
        );
    }

    if (loading && labels.length === 0) {
        return <Loading isLoading />;
    }

    const displayLabels = localLabels.length > 0 ? localLabels : labels;
    const noneLabel = displayLabels.find(label => label.name === 'ラベルなし');
    const otherLabels = displayLabels.filter(label => label.name !== 'ラベルなし');

    return (
        <div className={formShell.form}>
            {!loading && !error && noneLabel && (
                <div className={formShell.section}>
                    <p className={formShell.label}>デフォルトラベル</p>
                    <LabelCard label={noneLabel} readOnly />
                </div>
            )}

            {!loading && !error && (
                <div className={formShell.section}>
                    <p className={formShell.label}>カスタムラベル</p>
                    <div>
                        <Button
                            variant="primary"
                            size="cta"
                            onClick={handleAddNew}
                            disabled={loading}
                           
                        >
                            <span className="material-symbols-outlined text-icon-control">add</span>
                            ラベルを追加
                        </Button>
                    </div>
                    {otherLabels.length > 0 && (
                        <div className={formShell.gridAuto}>
                            {otherLabels.map(label => (
                                <LabelCard
                                    key={label.id}
                                    label={label}
                                    onEdit={() => handleEdit(label)}
                                    onDelete={handleDelete}
                                />
                            ))}
                        </div>
                    )}
                </div>
            )}

            {!loading && !error && displayLabels.length === 0 && (
                <p className={formShell.hint}>
                    ラベルが登録されていません。新しいラベルを追加してください。
                </p>
            )}

            <FormActions
                hasChanges={hasChanges()}
                loading={loading}
                onReset={handleReset}
                onSave={handleSave}
                submitType="button"
            />

            <Modal
                isOpen={showModal}
                showCloseButton={false}
                onClose={handleCancel}
                title={editingLabel ? 'ラベルを編集' : 'ラベルを追加'}
                align="left"
            >
                <LabelForm
                    onSubmit={handleSubmit}
                    initialData={editingLabel}
                    onCancel={handleCancel}
                    isDuplicateLabel={(name, excludeId) => {
                        return localLabels.some(label => {
                            if (excludeId && label.id === excludeId) {
                                return false;
                            }
                            return label.name.toLowerCase().trim() === name.toLowerCase().trim();
                        });
                    }}
                />
            </Modal>
        </div>
    );
};

export default LabelManager;
